import { useState, useContext } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  TableContainer,
  TextField,
  Typography,
} from "@mui/material";
import { BlockOutlined, CheckCircleOutline, Close, Send } from "@mui/icons-material";
import { useOktaAuth } from "@okta/okta-react";
import { ModuleContext } from "../../context/ModuleContext";
import { UserModuleContext } from "../../context/UserModuleContext";

const WSU_RED = "#A60F2D";
const SUSPENDED_COLOR = WSU_RED;
const ACTIVE_COLOR = "#2e7d32";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": { borderColor: WSU_RED },
    "&.Mui-focused fieldset": { borderColor: WSU_RED },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: WSU_RED },
};

export const AccountStatus = () => {
  const [state, setState] = useState({ error: null, waiting: false, comment: "", message: "" });

  const { authState } = useOktaAuth();
  const { setModule } = useContext(ModuleContext);
  const { userModule, setUserModule } = useContext(UserModuleContext);

  const oktaStatus = userModule.userData["Okta Status"];
  const networkId = userModule.userData["Network ID"];
  const isSuspended = oktaStatus === "SUSPENDED";

  const suspendAccount = async () => {
    setState((prev) => ({ ...prev, waiting: true }));

    try {
      const res = await fetch(`${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/user/suspend`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", Authorization: authState.accessToken.accessToken },
        body: JSON.stringify({ networkId, comment: state.comment, callerId: authState.accessToken.claims.sub }),
      });

      if (!res.ok) {
        const text = await res.text();
        const errorObj = JSON.parse(text);
        setState((prev) => ({ ...prev, waiting: false, error: { message: errorObj.error.message } }));
        return;
      }

      await res.json();
      setUserModule((prev) => ({ ...prev, userData: { ...prev.userData, "Okta Status": "SUSPENDED" } }));
      setState((prev) => ({ ...prev, waiting: false, comment: "", message: "User Suspended", error: null }));
    } catch (error) {
      setState((prev) => ({ ...prev, waiting: false, error }));
    }
  };

  const unsuspendAccount = async () => {
    setState((prev) => ({ ...prev, waiting: true }));

    try {
      const res = await fetch(`${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/user/unsuspend`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", Authorization: authState.accessToken.accessToken },
        body: JSON.stringify({ networkId, comment: state.comment, callerId: authState.accessToken.claims.sub }),
      });

      if (!res.ok) {
        const text = await res.text();
        const errorObj = JSON.parse(text);
        setState((prev) => ({ ...prev, waiting: false, error: { message: errorObj.error.message } }));
        return;
      }

      await res.json();
      setUserModule((prev) => ({ ...prev, userData: { ...prev.userData, "Okta Status": "ACTIVE" } }));
      setState((prev) => ({ ...prev, waiting: false, comment: "", message: "User Activated", error: null }));
    } catch (error) {
      setState((prev) => ({ ...prev, waiting: false, error }));
    }
  };

  if (state.waiting) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
      </Box>
    );
  }

  if (oktaStatus !== "ACTIVE" && oktaStatus !== "SUSPENDED") {
    return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
            Account Status
          </Typography>
          <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>{networkId}</Typography>
        </Box>
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            User {networkId} is in account status {oktaStatus} and is not eligible for this action.
          </Typography>
        </Box>
      </TableContainer>
    );
  }

  return (
    <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>

      {/* Success Banner */}
      {state.message && (
        <Box sx={{ backgroundColor: isSuspended ? SUSPENDED_COLOR : ACTIVE_COLOR, color: "white", px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          {isSuspended ? <BlockOutlined fontSize="small" /> : <CheckCircleOutline fontSize="small" />}
          <Typography variant="subtitle2" fontWeight={700}>{state.message}</Typography>
        </Box>
      )}

      {/* Error Banner */}
      {state.error && (
        <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Action failed</Typography>
          <Typography variant="body2">{state.error.message}</Typography>
        </Box>
      )}

      {/* ── Section: Account Status ── */}
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
          Account Status
        </Typography>
        <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>{networkId}</Typography>
      </Box>

      <Box sx={{ px: 3, py: 2 }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 0.75,
            borderRadius: 2,
            backgroundColor: isSuspended ? "#fce4ec" : "#e8f5e9",
            border: `1px solid ${isSuspended ? WSU_RED : ACTIVE_COLOR}`,
          }}
        >
          {isSuspended
            ? <BlockOutlined fontSize="small" sx={{ color: WSU_RED }} />
            : <CheckCircleOutline fontSize="small" sx={{ color: ACTIVE_COLOR }} />}
          <Typography variant="body2" fontWeight={600} sx={{ color: isSuspended ? WSU_RED : ACTIVE_COLOR }}>
            {isSuspended ? "Suspended" : "Active"}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mx: 3 }} />

      {/* ── Section: Justification ── */}
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
          Justification
        </Typography>
      </Box>

      <Box sx={{ px: 3, pb: 3 }}>
        <TextField
          required
          fullWidth
          size="small"
          multiline
          rows={3}
          label="Comment"
          value={state.comment}
          onChange={(e) => setState((prev) => ({ ...prev, comment: e.target.value }))}
          placeholder={`Provide a reason for ${isSuspended ? "unsuspending" : "suspending"} this account`}
          sx={fieldSx}
        />
      </Box>

      {/* ── Actions ── */}
      <Box sx={{ px: 3, pb: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button
          variant="outlined"
          endIcon={<Close />}
          onClick={() => setModule((prev) => ({ ...prev, isVisible: false }))}
          sx={{ borderColor: "#ccc", color: "#666", "&:hover": { borderColor: "#aaa", backgroundColor: "#f9f9f9" } }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          endIcon={<Send />}
          disabled={!state.comment.trim()}
          onClick={isSuspended ? unsuspendAccount : suspendAccount}
          sx={{
            backgroundColor: isSuspended ? ACTIVE_COLOR : SUSPENDED_COLOR,
            color: "white",
            px: 4,
            py: 1.25,
            fontWeight: 600,
            letterSpacing: 0.5,
            "&:hover": { backgroundColor: isSuspended ? "#1b5e20" : "#8a0c25" },
            "&.Mui-disabled": { backgroundColor: "#ddd", color: "#aaa" },
          }}
        >
          {isSuspended ? "Unsuspend Account" : "Suspend Account"}
        </Button>
      </Box>

    </TableContainer>
  );
};
